import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { products } from "@/lib/products";
import { useAuth } from "./auth";
import { API_BASE_URL } from "@/lib/api";

/** Fetch with a timeout — prevents cold-start Render.com backend from freezing the page */
async function fetchWithTimeout(url: string, timeoutMs = 5000): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { signal: controller.signal });
    return res;
  } finally {
    clearTimeout(id);
  }
}

export type CartLine = { id: string; qty: number; variant?: string };

const CART_KEY = "rl_cart_v1";
const WISH_KEY = "rl_wishlist_v1";

function readStorage<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

// ---------- Cart ----------
type CartCtx = {
  items: CartLine[];
  count: number;
  subtotal: number;
  add: (id: string, qty?: number, variant?: string) => boolean;
  update: (id: string, qty: number) => void;
  remove: (id: string) => void;
  clear: () => void;
};
const CartContext = createContext<CartCtx | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartLine[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setItems(readStorage<CartLine[]>(CART_KEY, []));
    setHydrated(true);
  }, []);
  useEffect(() => {
    if (hydrated) window.localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items, hydrated]);

  const [dbProducts, setDbProducts] = useState(products);
  useEffect(() => {
    fetchWithTimeout(`${API_BASE_URL}/api/products`, 5000)
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error(`HTTP ${res.status}`);
      })
      .then((data) => {
        setDbProducts(data);
        // Migrate cart items from static IDs (1, 2, 3...) to database UUIDs
        setItems((prev) => {
          let modified = false;
          const next = prev.map((item) => {
            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.id);
            if (!isUuid) {
              const staticProd = products.find((p) => p.id === item.id);
              if (staticProd) {
                const dbProd = data.find((p: any) => p.name.toLowerCase() === staticProd.name.toLowerCase());
                if (dbProd) {
                  modified = true;
                  return { ...item, id: dbProd.id };
                }
              }
            }
            return item;
          });
          return modified ? next : prev;
        });
      })
      .catch((err) => {
        if (err?.name === "AbortError") {
          console.error("[CartProvider] Products fetch TIMED OUT (backend cold start?) — using static fallback");
        } else {
          console.error("[CartProvider] Products fetch failed:", err?.message ?? err);
        }
      });
  }, []);

  const { user } = useAuth();

  const add = useCallback((id: string, qty = 1, variant?: string) => {
    if (!user) {
      toast.error("Sign in required", {
        description: "Please sign in to add items to your cart."
      });
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
      // Navigate via window.location — avoids calling useNavigate at Provider level which causes re-render loops
      window.location.href = `/auth?redirect=${encodeURIComponent(currentPath)}`;
      return false;
    }
    setItems((prev) => {
      const existing = prev.find((i) => i.id === id);
      if (existing) return prev.map((i) => (i.id === id ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { id, qty, variant }];
    });
    const p = dbProducts.find((x) => x.id === id) || products.find((x) => x.id === id);
    toast.success("Added to cart", { description: p?.name ?? "Your candle is waiting." });
    return true;
  }, [dbProducts, user]);
  const update = useCallback((id: string, qty: number) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty: Math.max(1, qty) } : i)));
  }, []);
  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    toast("Removed from cart");
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const { count, subtotal } = useMemo(() => {
    let c = 0, s = 0;
    for (const line of items) {
      const p = dbProducts.find((x) => x.id === line.id) || products.find((x) => x.id === line.id) || products.find((x) => x.name.toLowerCase() === line.id.toLowerCase());
      if (!p) continue;
      c += line.qty;
      s += line.qty * p.price;
    }
    return { count: c, subtotal: s };
  }, [items, dbProducts]);

  return <CartContext.Provider value={{ items, count, subtotal, add, update, remove, clear }}>{children}</CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be inside CartProvider");
  return ctx;
}

// ---------- Wishlist ----------
type WishCtx = {
  ids: string[];
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  remove: (id: string) => void;
};
const WishContext = createContext<WishCtx | null>(null);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [ids, setIds] = useState<string[]>([]);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setIds(readStorage<string[]>(WISH_KEY, []));
    setHydrated(true);
  }, []);
  useEffect(() => {
    if (hydrated) window.localStorage.setItem(WISH_KEY, JSON.stringify(ids));
  }, [ids, hydrated]);

  // Migrate wishlist static IDs to database UUIDs
  useEffect(() => {
    fetchWithTimeout(`${API_BASE_URL}/api/products`, 5000)
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error(`HTTP ${res.status}`);
      })
      .then((data) => {
        setIds((prev) => {
          let modified = false;
          const next = prev.map((id) => {
            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
            if (!isUuid) {
              const staticProd = products.find((p) => p.id === id);
              if (staticProd) {
                const dbProd = data.find((p: any) => p.name.toLowerCase() === staticProd.name.toLowerCase());
                if (dbProd) {
                  modified = true;
                  return dbProd.id;
                }
              }
            }
            return id;
          });
          return modified ? next : prev;
        });
      })
      .catch((err) => {
        if (err?.name === "AbortError") {
          console.error("[WishlistProvider] Products fetch TIMED OUT — using static fallback");
        } else {
          console.error("[WishlistProvider] Products fetch failed:", err?.message ?? err);
        }
      });
  }, []);

  const has = useCallback((id: string) => ids.includes(id), [ids]);
  const toggle = useCallback((id: string) => {
    setIds((prev) => {
      const exists = prev.includes(id);
      const p = products.find((x) => x.id === id);
      const title = exists ? "Removed from wishlist" : "Saved to wishlist";
      
      queueMicrotask(() => {
        toast(title, {
          id: `wishlist-toast-${id}`,
          description: p?.name,
        });
      });

      return exists ? prev.filter((x) => x !== id) : [...prev, id];
    });
  }, []);
  const remove = useCallback((id: string) => setIds((prev) => prev.filter((x) => x !== id)), []);

  return <WishContext.Provider value={{ ids, has, toggle, remove }}>{children}</WishContext.Provider>;
}
export function useWishlist() {
  const ctx = useContext(WishContext);
  if (!ctx) throw new Error("useWishlist must be inside WishlistProvider");
  return ctx;
}
